import React, { useState, useRef, useEffect } from "react";
import { SendIcon, RefreshCcwIcon } from "lucide-react";
import { Button } from "@/components/ui/button";

export interface ChatInputProps {
  onSendMessage: (message: string) => void;
  isLoading: boolean;
  onFocus?: () => void;
  brandColor?: string;
  isPublic?: boolean;
  isMobile?: boolean;
  onReset?: () => void;
  canReset?: boolean;
  isDisabled?: boolean;
  placeholder?: string;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  isLoading,
  onFocus,
  brandColor = "#060520",
  onReset,
  canReset = false,
  isDisabled = false,
  placeholder = "Type your message... (Shift+Enter for new line)",
}) => {
  const [message, setMessage] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [wasDisabledDueToLoading, setWasDisabledDueToLoading] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = () => {
    if (message.trim() && !isLoading && !isDisabled) {
      onSendMessage(message.trim());
      setMessage("");
      // Reset textarea height after sending
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Send message on Enter, but allow Shift+Enter for new lines
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (onFocus) onFocus();
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  // Auto-resize textarea based on content
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      const scrollHeight = textarea.scrollHeight;
      // Limit height to max 120px (about 4-5 lines)
      textarea.style.height = `${Math.min(scrollHeight, 120)}px`;
    }
  }, [message]);

  // Track when input gets disabled due to loading to restore focus later
  useEffect(() => {
    if (isLoading && isFocused) {
      setWasDisabledDueToLoading(true);
    }
  }, [isLoading, isFocused]);

  // Restore focus when loading finishes and input was previously focused
  useEffect(() => {
    if (
      !isLoading &&
      !isDisabled &&
      wasDisabledDueToLoading &&
      textareaRef.current
    ) {
      // Small delay to ensure the DOM has updated
      const timeoutId = setTimeout(() => {
        textareaRef.current?.focus();
        setWasDisabledDueToLoading(false);
      }, 100);

      // Cleanup function to clear timeout if component unmounts
      return () => clearTimeout(timeoutId);
    }
  }, [isLoading, isDisabled, wasDisabledDueToLoading]);

  // Generate styles for the border
  const containerStyle = {
    border: isFocused ? `3px solid ${brandColor}` : "2px solid #e5e7eb",
    backgroundColor: "white",
    borderRadius: "20px",
    position: "relative" as const,
    minHeight: "52px", // Increased for better text centering
    transition: "border-color 0.2s ease, border-width 0.2s ease",
  };

  return (
    <div className="w-full px-4 py-3 bg-transparent">
      {/* Chat Input Container */}
      <div className="flex items-end gap-3">
        <div
          className={`
            flex-1 relative rounded-lg overflow-hidden
            ${isDisabled ? "opacity-50" : ""}
          `}
          style={containerStyle}
        >
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={isLoading || isDisabled}
            placeholder={placeholder}
            className="w-full bg-transparent focus:outline-none resize-none overflow-hidden focus:ring-0 focus:border-none"
            style={{
              border: "none",
              outline: "none",
              boxShadow: "none",
              WebkitAppearance: "none",
              MozAppearance: "none",
              paddingLeft: isFocused ? "17px" : "18px", // Adjust for border width (3px vs 2px)
              paddingRight: isFocused ? "59px" : "60px", // Adjust for border width
              paddingTop: isFocused ? "14px" : "15px", // Better balance for vertical centering
              paddingBottom: isFocused ? "14px" : "15px", // Better balance for vertical centering
              fontSize: "16px", // Prevent iOS Safari auto-zoom (must be 16px or larger)
              lineHeight: "1.3", // Slightly reduced for better text positioning
              minHeight: "24px",
              maxHeight: "120px",
              transition: "padding 0.2s ease",
              verticalAlign: "middle", // Helps with text positioning
            }}
            autoComplete="off"
            rows={1}
          />

          <Button
            type="button"
            onClick={handleSend}
            disabled={isLoading || !message.trim() || isDisabled}
            className="absolute right-2 bottom-2 p-0 w-9 h-9 rounded-full"
            style={{
              backgroundColor: brandColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            aria-label="Send message"
          >
            <SendIcon size={16} className="text-white" />
          </Button>
        </div>

        {canReset && onReset && (
          <Button
            variant="outline"
            size="icon"
            onClick={onReset}
            disabled={isLoading}
            className="h-12 w-12 flex-shrink-0 rounded-full border border-gray-200 bg-white"
            title="Start a new conversation"
          >
            <RefreshCcwIcon size={18} />
          </Button>
        )}
      </div>
    </div>
  );
};
